import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "juou-card" }

import {
  useBaseAsync,
  type HpsfirProTableColumn,
  type HpsfirProTableData,
  type HpsfirProTableFilter,
  type HpsfirProTableGetDataFunction,
  type HpsfirProFormItem,
} from 'hpsfir-managebase-ui';

import { useApi } from '../../../hooks/useApi';
import { ElMessage } from 'element-plus';
import { ref } from 'vue';
import TicketDialog from './TicketDialog.vue';
import { normalizeBizTableColumns } from '@/utils/tableColumns';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const columns = ref<HpsfirProTableColumn[]>(normalizeBizTableColumns([]));
const tableRef = ref<any>();
const generateTicketFormRef = ref<any>();
const currentPackageId = ref<number>();
const ticketDialogRef = ref<InstanceType<typeof TicketDialog>>();

const addInitValue = {
  // 默认值
};

const conditionsInitValue = {
  // 搜索条件默认值
};

const generateTicketInitValue = {
  validDays: 365,
  count: 1,
};

const generateTicketItems = ref<HpsfirProFormItem[]>([
  {
    name: 'count',
    label: '点券数量',
    type: 'number',
    rule: [
      { required: true, message: '请输入点券数量' },
      { type: 'number', min: 1, message: '数量必须大于0' },
    ],
  },
  {
    name: 'validDays',
    label: '有效期(天)',
    type: 'number',
    rule: [
      { required: true, message: '请输入有效期' },
      { type: 'number', min: 1, message: '有效期必须大于0' },
    ],
  },
]);

// 初始化列配置
const init = useBaseAsync(() => {
  columns.value = normalizeBizTableColumns([
    {
      name: 'sequenceNumber',
      label: '序号',
      type: 'number',
      isCondition: true,
      rule: [{ required: true, message: '请输入排序序号' }],
      width: 120,
    },
    {
      name: 'beanCount',
      label: 'AI豆数',
      type: 'number',
      isCondition: true,
      rule: [{ required: true, message: '请输入AI豆数' }],
      width: 120,
    },
    {
      name: 'totalTicketCount',
      label: '总点券数',
      type: 'number',
      hideEdit: true,
      hideCreate: true,
      width: 120,
    },
    {
      name: 'verifiedTicketCount',
      label: '已核销',
      type: 'number',
      hideEdit: true,
      hideCreate: true,
      width: 120,
    },
    {
      name: 'unverifiedTicketCount',
      label: '未核销',
      type: 'number',
      hideEdit: true,
      hideCreate: true,
      width: 120,
    },
    {
      name: 'createTime',
      label: '创建时间',
      type: 'string',
      hideEdit: true,
      hideCreate: true,
      width: 180,
    },
  ]);
});

const operates: any[] = [
  {
    key: 'generate',
    name: '生成点券',
    handler: useBaseAsync((row: any) => {
      currentPackageId.value = row.id;
      generateTicketFormRef.value?.showDialog();
    }),
  },
  {
    key: 'viewTickets',
    name: '查看点券',
    handler: useBaseAsync((row: any) => {
      ticketDialogRef.value?.open(row.id);
    }),
  },
];

const loading = ref(false);

// 获取数据
const getData = useBaseAsync<HpsfirProTableGetDataFunction>(async(filter: HpsfirProTableFilter) => {
  try {
    loading.value = true;
    const res = await useApi().ticketPackageApi.pageTicketPackageticketPackage({
      conditions: Object.entries(filter.conditions).reduce((conditions, [key, val]) => {
        if (val === null || val === undefined || val === '') {
          return conditions;
        }
        conditions[key] = val;
        return conditions;
      }, {} as any),
      pageQuery: {
        page: filter.paginations.page - 1,
        pageSize: filter.paginations.pageSize,
      },
    });

    const tableData: HpsfirProTableData = {
      total: res.data.data?.total || 0,
      data: res.data.data?.data?.map((item: any) => ({
        ...item,
        unverifiedTicketCount: item.totalTicketCount - item.verifiedTicketCount,
      })) || [],
    };
    return tableData;
  } finally {
    loading.value = false;
  }
});

// 格式化删除确认信息
const formatDeleteInfo = (row: any) => `您确定要删除点券套餐"AI豆:${row.beanCount || '未知'}"吗？此操作不可恢复，请谨慎操作。`;

// 添加点券套餐
const addFun = useBaseAsync(async(data: any) => {
  await useApi().ticketPackageApi.addTicketPackageticketPackage({
    ticketPackageAdminVo: {
      ...data,
    },
  });
  ElMessage.success('添加成功');
});

// 更新点券套餐
const updateFun = useBaseAsync(async(data: any) => {
  await useApi().ticketPackageApi.updateTicketPackageticketPackage({
    ticketPackageAdminVo: {
      ...data,
    },
  });
  ElMessage.success('修改成功');
});

// 删除点券套餐
const deleteFun = useBaseAsync(async(data: any) => {
  await useApi().ticketPackageApi.delTicketPackageticketPackage(data.id);
  ElMessage.success('删除成功');
});

// 处理生成点券
const handleGenerateTickets = useBaseAsync(async(data: any) => {
  try {
    // 计算过期时间: 当前时间 + validDays天
    const expireDate = new Date();
    expireDate.setDate(expireDate.getDate() + data.validDays);
    // 转为ISO格式的字符串
    const expireTime = expireDate.toISOString();

    await useApi().ticketApi.generateTicketsticket({
      ticketPackageId: currentPackageId.value!,
      count: data.count,
      // 使用正确的参数名
      expireTime,
    });
    ElMessage.success(`成功生成${data.count}张点券`);
    tableRef.value?.refresh();
  } catch (error) {
    console.error('生成点券失败:', error);
    ElMessage.error('生成点券失败');
  }
});

// 页面初始化
init();

return (_ctx: any,_cache: any) => {
  const _component_HpsfirProTable = _resolveComponent("HpsfirProTable")!
  const _component_HpsfirProForm = _resolveComponent("HpsfirProForm")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_HpsfirProTable, {
      ref_key: "tableRef",
      ref: tableRef,
      "export-file-name": "点券套餐管理",
      title: "点券套餐管理",
      paginations: { pageSize: 10 },
      columns: columns.value,
      "get-data": _unref(getData),
      "on-add": _unref(addFun),
      "on-delete": _unref(deleteFun),
      "on-edit": _unref(updateFun),
      loading: loading.value,
      "is-dangder-delete": true,
      "format-dangder-delete-info": formatDeleteInfo,
      "row-key": "id",
      operates: operates,
      "add-init-values": addInitValue,
      "codition-init-values": conditionsInitValue
    }, null, 8, ["columns", "get-data", "on-add", "on-delete", "on-edit", "loading"]),
    _createVNode(_component_HpsfirProForm, {
      ref_key: "generateTicketFormRef",
      ref: generateTicketFormRef,
      "is-dialog": true,
      title: "生成点券",
      items: generateTicketItems.value,
      "init-form-value": generateTicketInitValue,
      onOk: _unref(handleGenerateTickets)
    }, null, 8, ["items", "onOk"]),
    _createVNode(TicketDialog, {
      ref_key: "ticketDialogRef",
      ref: ticketDialogRef
    }, null, 512)
  ]))
}
}

})